/**
 * Map Projection Web App - Main Entry Point
 *
 * This is the application's initialization point.
 * It will eventually:
 * 1. Load and parse world.svg
 * 2. Initialize PixiJS renderer
 * 3. Set up Earth model and projections
 * 4. Start render loop
 */

console.log('Map Projection Web App initializing...');

// Import core math modules
import * as projections from './core/projections.js';
import { createRotationMatrix, rotatePoints } from './core/rotation.js';
import { generateSphere } from './core/sphere.js';

// Import Phase 3 modules - SVG parsing and sphere conversion
import { loadCountryData, validateCountryData } from './core/country-sphere.js';

// Phase 4 imports - Rendering system
import { createDualRenderer } from './renderer/renderer.js';
import { EarthModel } from './core/earth-model.js';
import { renderAllCountries } from './renderer/projection-renderer.js';
import { renderGlobeCanvas2D } from './renderer/globe-renderer.js';

// Phase 5 imports - UI and mouse interaction
import { initControls } from './ui/controls.js';
import { initNav } from './ui/nav.js';
import { on } from './core/event-bus.js';
import { countryColor } from './core/svg-parser.js';

// View system
import { ViewManager }      from './core/view-manager.js';
import { MapProjectionView, LAYOUT } from './views/map-projection-view.js';
import { BackgroundGeneratorView }   from './views/background-generator-view.js';
import { calculateOptimalSizes, calculatePositions } from './core/layout-calculator.js';

/**
 * Application state
 */
const app = {
    renderer: null,  // Phase 6: Now contains { projection, globe, app, PIXI }
    earthModel: null,
    countryData: null,  // Phase 3: Country boundary data with 2D and 3D coords
    isPlaying: false,
    lastTime: 0,
    colorIdx: 0,
    graphicsCacheProjection: null,  // Phase 6: Separate caches for dual rendering
    graphicsCacheGlobe: null,
    isDirty: true,  // Phase 6 Optimization: Track if render is needed
    hitTestCache: null,      // Populated each frame for projection hover hit testing
    hoveredCountryId: null,  // Set by country-hover.js, consumed by renderAllCountries
    viewManager: null,
};

/**
 * Initialize the application
 */
async function init() {
    console.log('Initializing application...');

    try {
        // Phase 4.1 + Phase 6: Initialize dual PixiJS renderer (projection + globe)
        console.log('\n=== Phase 4.1/6: Initializing Dual Renderer ===');
        const container = document.getElementById('canvas-container');

        // Calculate optimal dimensions for dual rendering
        const initialProjection = 'mercator'; // Default projection
        const ratio = projections.RATIOS[initialProjection];
        const sizes = calculateOptimalSizes(
            window.innerWidth,
            window.innerHeight,
            ratio,
            LAYOUT
        );
        const positions = calculatePositions(sizes, window.innerWidth, LAYOUT);

        app.renderer = await createDualRenderer(
            container,
            sizes.projectionWidth,
            sizes.projectionHeight,
            sizes.globeSize,
            initialProjection,
            positions,
            LAYOUT
        );
        app.graphicsCacheProjection = new Map();  // Separate cache for 2D projection
        app.graphicsCacheGlobe = new Map();  // Separate cache for globe
        app.hitTestCache = new Map();
        app.viewManager = new ViewManager();
        app.viewManager.register('dual', new MapProjectionView(app));
        app.viewManager.register('background-generator', new BackgroundGeneratorView(app));
        app.viewManager.switchTo('dual');
        console.log('✓ Dual renderer initialized (projection + globe)');

        // Phase 3: Load country data from SVG
        console.log('\n=== Phase 3: Loading Country Data ===');
        const countryData = await loadCountryData('/world.svg');
        app.countryData = countryData;

        console.log(`✓ Loaded ${countryData.countries.size} countries`);

        // Validate the data
        const issues = validateCountryData(countryData);
        if (issues.length > 0) {
            console.warn(`Found ${issues.length} validation issues:`, issues.slice(0, 5));
        } else {
            console.log('✓ All country data validated successfully');
        }

        // Phase 4.3: Create Earth model
        console.log('\n=== Phase 4.3: Creating Earth Model ===');
        app.earthModel = new EarthModel(countryData);

        // Set canvas dimensions for 2D projection rendering (use already-calculated sizes)
        app.earthModel.setCanvasDimensions(app.renderer.projection.width, app.renderer.projection.height);
        console.log(`✓ Earth model created (projection: ${app.earthModel.projection})`);

        // Wire up event bus subscriptions
        on('render:dirty',         ()           => renderFrame(true));
        on('projection:change',    ({ name })   => setProjection(name));
        on('rotation:toggle-play', ()           => { app.isPlaying = !app.isPlaying; });
        on('rotation:reset',       ()           => resetRotation());
        on('colors:randomize',     ()           => randomizeColors());

        // Initial render
        console.log('\n=== Rendering Initial Frame ===');
        renderFrame();
        console.log('✓ Initial render complete');

        // Test the math modules
        testMathModules();

        // Phase 5: Initialize UI controls and mouse handler
        console.log('\n=== Phase 5: Initializing UI ===');
        initControls(app);
        initNav(app);
        console.log('✓ UI initialized');

        // Hide loading indicator
        document.getElementById('loading').style.display = 'none';

        console.log('\n✓ Phases 1-5 Initialization complete!');
        console.log('  → Try: drag mouse to rotate, or use UI controls on the right');
        console.log('  → Console API: setProjection("miller"), setRotation(45, 0, 0), startRotation()');
    } catch (error) {
        console.error('Initialization failed:', error);
        document.getElementById('loading').textContent = 'Error: ' + error.message;
    }
}

/**
 * Render current frame (dual rendering: projection + globe)
 * @param {boolean} force - Force render even if not dirty
 */
function renderFrame(force = false) {
    if (!app.earthModel || !app.renderer) {
        console.warn('Cannot render: earthModel or renderer not initialized');
        return;
    }

    // Phase 6 Optimization: Skip rendering if nothing changed (unless forced or playing)
    if (!force && !app.isDirty && !app.isPlaying) {
        return;
    }

    // Pre-compute rotated 3D coordinates for all paths (CRITICAL OPTIMIZATION)
    // Avoids duplicate rotatePoints() calls in both renderers
    const rotationCache = new Map();
    for (const [countryId, country] of app.countryData.countries) {
        const rotatedPaths = [];
        for (const path of country.paths) {
            const rotated3D = rotatePoints(path.sphere3D, app.earthModel.rotationMatrix);
            rotatedPaths.push(rotated3D);
        }
        rotationCache.set(countryId, rotatedPaths);
    }

    // Render 2D projection (left side) - uses cached rotations
    renderAllCountries(
        app.renderer.projection,
        app.countryData.countries,
        app.earthModel,
        app.graphicsCacheProjection,
        rotationCache,
        app.hoveredCountryId,
        app.hitTestCache
    );

    // Render globe (center) using Canvas 2D - uses same cached rotations
    renderGlobeCanvas2D(
        app.renderer.globe.ctx,
        app.renderer.globe.radius,
        app.countryData.countries,
        rotationCache
    );

    // Mark as clean after rendering
    app.isDirty = false;
}

/**
 * Test projection, rotation, and sphere generation functions
 */
function testMathModules() {
    console.log('\n=== Testing Core Math Modules ===');

    // Test projections
    console.log('\n1. Testing Projections:');
    const lat = 0.5; // ~28.6 degrees
    const lon = 1.0; // ~57.3 degrees

    const [x, y] = projections.mercator(lat, lon);
    console.log(`   Mercator(${lat.toFixed(4)}, ${lon.toFixed(4)}) = [${x.toFixed(6)}, ${y.toFixed(6)}]`);

    const [lat2, lon2] = projections.mercatorInverse(x, y);
    console.log(`   Inverse: [${lat2.toFixed(6)}, ${lon2.toFixed(6)}]`);
    console.log(`   Round-trip error: lat=${Math.abs(lat - lat2).toExponential(2)}, lon=${Math.abs(lon - lon2).toExponential(2)}`);

    // Test rotation
    console.log('\n2. Testing Rotation Matrix:');
    const R = createRotationMatrix(0, 0, 0);
    console.log(`   Identity matrix (0,0,0):`, R);

    const R90 = createRotationMatrix(0, 0, 90);  // gamma=90 for Z-axis
    const point = new Float32Array([1, 0, 0]);
    const rotated = rotatePoints(point, R90);
    console.log(`   90° Z-rotation: (1,0,0) → (${rotated[0].toFixed(4)}, ${rotated[1].toFixed(4)}, ${rotated[2].toFixed(4)})`);

    // Test sphere generation
    console.log('\n3. Testing Sphere Generation:');
    const sphereSmall = generateSphere(10, 5, 'equirectangular');
    console.log(`   Small grid (10×5): ${sphereSmall.length} coordinates (${(sphereSmall.length * 4 / 1024).toFixed(2)} KB)`);

    const r = Math.sqrt(sphereSmall[0]**2 + sphereSmall[1]**2 + sphereSmall[2]**2);
    console.log(`   First point: (${sphereSmall[0].toFixed(4)}, ${sphereSmall[1].toFixed(4)}, ${sphereSmall[2].toFixed(4)}), radius=${r.toFixed(4)}`);

    // Test all projection types
    console.log('\n4. Available Projections:');
    console.log(`   PROJECTIONS:`, Object.keys(projections.PROJECTIONS));
    console.log(`   PROJECTIONS_INVERSE:`, Object.keys(projections.PROJECTIONS_INVERSE));
    console.log(`   RATIOS:`, projections.RATIOS);

    console.log('\n✓ Math modules initialized and tested!');
    console.log('  → Open test.html for comprehensive test suite\n');
}

/**
 * Animation loop
 */
function animate(currentTime) {
    requestAnimationFrame(animate);

    if (!app.earthModel) return;

    // Update angle display every frame (combined format)
    if (app.controls && app.controls.anglesCombined) {
        const angles = app.earthModel.getAngles();
        app.controls.anglesCombined.textContent =
            `(${angles.alpha.toFixed(1)}, ${angles.beta.toFixed(1)}, ${angles.gamma.toFixed(1)})°`;
    }

    // Update rotation based on speeds (only when playing)
    if (app.isPlaying) {
        const deltaTime = (currentTime - app.lastTime) / 1000;
        app.lastTime = currentTime;
        app.earthModel.update(deltaTime);
        app.isDirty = true;  // Mark dirty when rotation changes
    } else {
        // Keep lastTime current even when paused to avoid jumps on resume
        app.lastTime = currentTime;
    }

    // Render both projection and globe (only if dirty or playing)
    renderFrame();
}

/**
 * Handle window resize
 */
function onResize() {
    if (!app.viewManager) return;
    app.viewManager.applyLayout();
    app.isDirty = true;
    renderFrame(true);
}

/**
 * Change projection type
 * @param {string} projectionName - One of: mercator, miller, lambert, equirectangular
 */
function setProjection(projectionName) {
    if (!app.earthModel) {
        console.error('Earth model not initialized');
        return;
    }

    console.log(`Switching to ${projectionName} projection`);

    app.earthModel.setProjection(projectionName);
    app.viewManager.applyLayout();
    app.isDirty = true;
    renderFrame(true);

    console.log(`✓ Projection changed to ${projectionName}`);
}

/**
 * Set rotation angles
 * @param {number} alpha - Z-axis rotation (degrees)
 * @param {number} beta - Y-axis rotation (degrees)
 * @param {number} gamma - X-axis rotation (degrees)
 */
function setRotation(alpha, beta, gamma) {
    if (!app.earthModel) {
        console.error('Earth model not initialized');
        return;
    }

    app.earthModel.setRotation(alpha, beta, gamma);
    app.isDirty = true;
    renderFrame(true);

    console.log(`✓ Rotation set to (${alpha}°, ${beta}°, ${gamma}°)`);
}

/**
 * Set rotation speeds and start rotating
 * @param {number} alphaSpeed - Z-axis speed (deg/sec)
 * @param {number} betaSpeed - Y-axis speed (deg/sec)
 * @param {number} gammaSpeed - X-axis speed (deg/sec)
 */
function startRotation(alphaSpeed = 0, betaSpeed = 10, gammaSpeed = 0) {
    if (!app.earthModel) {
        console.error('Earth model not initialized');
        return;
    }

    app.earthModel.setRotationSpeeds(alphaSpeed, betaSpeed, gammaSpeed);
    app.isPlaying = true;

    console.log(`✓ Rotation started (speeds: ${alphaSpeed}°/s, ${betaSpeed}°/s, ${gammaSpeed}°/s)`);
}

/**
 * Stop rotation
 */
function stopRotation() {
    app.isPlaying = false;
    if (app.earthModel) {
        app.earthModel.setRotationSpeeds(0, 0, 0);
    }
    console.log('✓ Rotation stopped');
}

/**
 * Cycle to the next deterministic color palette (10 palettes total)
 */
function randomizeColors() {
    if (!app.countryData?.countries) return;
    app.colorIdx = (app.colorIdx + 1) % 10;
    app.countryData.countries.forEach((country) => {
        country.color = countryColor(country.name, app.colorIdx);
    });
    renderFrame(true);
}

/**
 * Reset rotation to identity
 */
function resetRotation() {
    if (!app.earthModel) {
        console.error('Earth model not initialized');
        return;
    }

    app.earthModel.resetRotation();
    app.isPlaying = false;
    app.isDirty = true;
    renderFrame(true);

    console.log('✓ Rotation reset to (0°, 0°, 0°)');
}

// Expose functions for console testing
window.setProjection = setProjection;
window.setRotation = setRotation;
window.startRotation = startRotation;
window.stopRotation = stopRotation;
window.resetRotation = resetRotation;
window.app = app;

// Event listeners
window.addEventListener('resize', onResize);

// Start the application
init().then(() => {
    // Start animation loop
    app.lastTime = performance.now();
    animate(app.lastTime);
});
